import { GetStaticPaths, GetStaticProps, NextPage } from 'next';
import Head from 'next/head';
import { groq } from 'next-sanity';
import React from 'react';
import GrayLink from '../../components/GrayLink';
import ArticleMedia from '../../components/learn/articles/ArticleMedia';
import RecommendedArticles from '../../components/learn/articles/RecommendedArticles';
import SanityBody from '../../components/SanityBody';
import SocialMediaBar from '../../components/SocialMediaBar';
import { getClient } from '../../lib/sanity.server';
import { urlFor } from '../../lib/sanity';
import { Category } from './articles';
import { Article } from './articles/[slug]';

interface Header {
  title: string;
  seo: {
    description: string;
    imageUrl: string;
  };
}

interface Props {
  header: Header;
  article: Article;
  category: Category;
  relatedArticles: Array<Article>;
}

const ArticlePage: NextPage<Props> = ({ header, article, category, relatedArticles }) => {
  const imageURl = article.coverImageAsset ? urlFor(article.coverImageAsset).url() : null;
  const recommendedArticles = relatedArticles
    .filter(mappedArticle => mappedArticle.slug !== article.slug)
    .slice(0, 2);
  const maxWidth = article.coverVideoUrl ? 'max-w-6xl' : 'max-w-3xl';
  return (
    <>
      <Head>
        <title>{header.title}</title>
        <meta name='title' content={header.title} />
        <meta name='description' content={header.seo ? header.seo.description : ''} />
        <meta property='og:image' content={header.seo ? header.seo.imageUrl : ''} />
      </Head>
      <div className='mx-auto mb-32'>
        <div className={`mx-auto ${maxWidth}`}>
          <div className='mt-32 text-gray-50'>
            <GrayLink link='/learn' text='Learn' /> /&nbsp;
            <GrayLink link='/learn/articles' text='Online Ministry Resources' />
          </div>
          <h1 className='mt-2'>{article.title}</h1>
        </div>
        <div className='mt-16'>
          <ArticleMedia videoUrl={article.coverVideoUrl} imageUrl={imageURl} />
          <div className='max-w-3xl mx-auto mb-32'>
            <div>
              <SanityBody body={article.body} />
            </div>
            <div className='h-1 mt-16 bg-gray-10' />
            <div className='mt-6'>
              <SocialMediaBar />
            </div>
            <div className='mt-32'>
              <RecommendedArticles category={category} articles={recommendedArticles} />
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export const getStaticPaths: GetStaticPaths<{ slug: string }> = async () => {
  const paths = await getClient().fetch<Array<string>>(groq`
  *[_type == 'article' && defined(slug.current)][].slug.current
  `);

  return {
    paths: paths.map(slug => ({ params: { slug } })),
    fallback: 'blocking',
  };
};

export const getStaticProps: GetStaticProps<{
  header: Header;
  article: Article;
  category: Category;
  relatedArticles: Array<Article>;
}> = async ({ params, preview = false }) => {
  const header = await getClient(preview).fetch<Header>(
    groq`
    *[_type=="article" && slug.current==$slug][0] {
      title,
      seo {
        description,
        "imageUrl": openGraphImage.asset->url
      }
    }
    `,
    {
      slug: params?.slug,
    }
  );

  const article = await getClient(preview).fetch<Article>(
    groq`
    *[_type=="article" && slug.current==$slug][0] {
      "slug": slug.current,
      "coverVideoUrl": media[0]->.assetId,
      "coverImageAsset": media[0].asset,
      title,
      "category": category->.slug.current,
      body[]{
        ...,
        _type == "reference"=>^->
      },
    }
  `,
    {
      slug: params?.slug,
    }
  );

  if (!article) {
    return {
      notFound: true,
    };
  }

  const category = await getClient(preview).fetch<Category>(
    groq`
    *[_type=="articleCategory" && slug.current==$slug][0] {
      "slug": slug.current,
      title,
      description,
    }
  `,
    {
      slug: article.category,
    }
  );

  const articles = await getClient(preview).fetch<Array<Article>>(
    groq`
    *[_type=="article" && category->.slug.current==$slug] {
      "slug": slug.current,
      "coverVideoUrl": media[0]->.assetId,
      "coverImageAsset": media[0].asset,
      title,
      "category": category->.slug.current
    } [0..2]
  `,
    {
      slug: article.category,
    }
  );

  const articlesWithVideos = await Promise.all(
    articles.map(async article => {
      if (article.coverVideoUrl) {
        const response = await fetch(
          `http://fast.wistia.net/oembed?url=http://home.wistia.com/medias/${article.coverVideoUrl}?embedType=async&videoWidth=640`,
          {
            method: 'GET',
          }
        );

        const data = await response.json();
        article.coverVideoUrl = data.thumbnail_url;
      }
      return article;
    })
  );

  return {
    props: {
      header,
      article,
      category,
      relatedArticles: articlesWithVideos,
    },
  };
};

export default ArticlePage;
